import React from 'react';
import { t, Trans } from '@lingui/macro';

import { Box, Flex, Paragraph } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import HowRoverWorks, { ImageOptions } from '@rover/react-lib/src/components/HowRoverWorks';
import LandingHero, {
  HeroImageSrcSet,
} from '@rover/react-lib/src/pages/landing/components/LandingHero';
import GetDetailedUpdates from '@rover/react-lib/src/pages/landing/ServiceLanding/components/GetDetailedUpdates';
import Guarantees from '@rover/react-lib/src/pages/landing/ServiceLanding/components/Guarantees';
import ServiceLandingServices from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServices';
import ServiceTargetAudience from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceTargetAudience';
import ServiceValueProposition from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceValueProposition';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import CitiesAndNeighborhoods from '@rover/react-lib/src/pages/shared/CitiesAndNeighborhoods/CitiesAndNeighborhoods';
import {
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import ServiceLandingResultsLayout from '../../components/ServiceLandingResultsLayout';

import dogWalkingHeroImageDesktop from './images/Hero_Walking_desktop.jpg';
import dogWalkingHeroImageMobile from './images/Hero_Walking_mobile.jpg';
import dogWalkingHeroImageTablet from './images/Hero_Walking_tablet.jpg';

const dogWalkingImages: HeroImageSrcSet = {
  desktop: {
    src: dogWalkingHeroImageDesktop,
    height: 927,
  },
  tablet: {
    src: dogWalkingHeroImageTablet,
    height: 927,
  },
  mobile: {
    src: dogWalkingHeroImageMobile,
    height: 614,
  },
};

const DogWalking: React.FC<Props> = ({
  countryConfig,
  citiesAndNeighborhoods,
  language,
  serviceLinksWithDisplayName,
  serviceLandingData,
  searchResults,
  frontendConfigSettings,
  adwordslocation,
  serviceTypes,
}) => {
  const serviceLandingType = ServiceLandingEnum.DOG_WALKING;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <LandingHero mb="20x" heroImageSrcSet={dogWalkingImages}>
        <LandingHero.Content>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            language={language}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
          />
          <LandingHero.TrustPilot languageCode={language} />
        </LandingHero.Content>
      </LandingHero>
      {searchResults && searchResults.results.length && (
        <ServiceLandingResultsLayout
          serviceLandingType={serviceLandingType}
          language={language}
          countryConfig={countryConfig}
          searchResults={searchResults}
          mapSettings={frontendConfigSettings}
          adwordslocation={adwordslocation}
        />
      )}
      <Section mt="0x" mb="40x">
        <Flex alignItems="center" flexDirection="column">
          <ServiceValueProposition>
            <ServiceValueProposition.Display>
              <Trans>Finally—a dog walking option that works</Trans>
            </ServiceValueProposition.Display>
            <ServiceValueProposition.List>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Dog walkers you can trust</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Every dog walker has been individually reviewed and approved by Rover and 97% of
                    bookings have received a perfect 5-star rating.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Easy Meet & Greets</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Meet your dog's perfect match before you book. Request a free in-person Meet &
                    Greet, right from your Rover inbox.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
              <ServiceValueProposition.ListItem>
                <ServiceValueProposition.Subtitle>
                  <Trans>Easy scheduling</Trans>
                </ServiceValueProposition.Subtitle>
                <ServiceValueProposition.Text>
                  <Trans>
                    Choose the days of the week and times your dog needs a walk to keep your pup on
                    a schedule.
                  </Trans>
                </ServiceValueProposition.Text>
              </ServiceValueProposition.ListItem>
            </ServiceValueProposition.List>
          </ServiceValueProposition>

          <HowRoverWorks>
            <HowRoverWorks.Display>
              <Trans>How to find the perfect dog walker</Trans>
            </HowRoverWorks.Display>
            <HowRoverWorks.List>
              <HowRoverWorks.ListItem image={ImageOptions.MAGNIFYING_GLASS}>
                <HowRoverWorks.ItemHeading step={1}>
                  <Trans>Search Dog Walkers</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>Search to find dog walkers in your neighborhood.</Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
              <HowRoverWorks.ListItem image={ImageOptions.COLLAR}>
                <HowRoverWorks.ItemHeading step={2}>
                  <Trans>Meet In Person</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    Connect with a dog walker who's a good fit for you, your dog, and your
                    lifestyle.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
              <HowRoverWorks.ListItem image={ImageOptions.PHONE}>
                <HowRoverWorks.ItemHeading step={3}>
                  <Trans>Book and Pay</Trans>
                </HowRoverWorks.ItemHeading>
                <HowRoverWorks.ItemContent>
                  <Trans>
                    It’s that easy! All payments are processed through Rover's secured platform.
                  </Trans>
                </HowRoverWorks.ItemContent>
              </HowRoverWorks.ListItem>
            </HowRoverWorks.List>
          </HowRoverWorks>

          <GetDetailedUpdates
            headingText={t`Get detailed updates`}
            introText={t`With the Rover app, get a comprehensive Rover Card from your dog walker that includes:`}
            listItems={[
              <Paragraph key="0">
                <Trans comment="part of a list of updates owners can receive from a dog walker">
                  Start and stop times
                </Trans>
              </Paragraph>,
              <Paragraph key="1">
                <Trans comment="part of a list of updates owners can receive from a dog walker">
                  A map of their walk with total distance
                </Trans>
              </Paragraph>,
              <Paragraph key="2">
                <Trans comment="part of a list of updates owners can receive from a dog walker">
                  Pee, poo, food, and water breaks
                </Trans>
              </Paragraph>,
              <Paragraph key="3">
                <Trans comment="part of a list of updates owners can receive from a dog walker">
                  Cute photos and a personalized note
                </Trans>
              </Paragraph>,
            ]}
          />

          <Guarantees serviceTypeSlug={serviceTypeSlug} countryConfig={countryConfig} />

          <ServiceTargetAudience data-qa-id="service-landing-description">
            <ServiceTargetAudience.Display>
              <Trans>When nature calls, dog walkers on Rover answer</Trans>
            </ServiceTargetAudience.Display>
            <ServiceTargetAudience.Content>
              <Trans>
                You can't always predict a super busy day at work, but you can anticipate your dog's
                needs. Instead of rushing home at lunch, book a dog walker to give your dog a 30- or
                60-minute dog walk. Your dog walker can stop by as many times as you need—on
                whatever days you need them.
              </Trans>
            </ServiceTargetAudience.Content>
          </ServiceTargetAudience>

          <ServiceLandingServices {...{ serviceLandingType, serviceLinksWithDisplayName }}>
            <ServiceLandingServices.Display>
              <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
            </ServiceLandingServices.Display>
          </ServiceLandingServices>

          <CitiesAndNeighborhoods citiesOrNeighborhoods={citiesAndNeighborhoods}>
            <CitiesAndNeighborhoods.Display>
              <Trans>Thousands of dog sitters in cities and neighborhoods, including:</Trans>
            </CitiesAndNeighborhoods.Display>
          </CitiesAndNeighborhoods>

          <Box my="8x" data-qa-id="service-landing-download-app-callout">
            <AppStoreCallout />
          </Box>
        </Flex>
      </Section>
    </>
  );
};

export default DogWalking;
